import { useNavigation, useDelete } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Space, Popconfirm, Switch, Tag, message, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface CancellationReason {
  id: number;
  actorType: 'CLIENT' | 'DRIVER';
  reasonUz: string;
  reasonRu: string;
  reasonEn: string;
  sortOrder: number;
  isActive: boolean;
}

export const CancellationReasonList = () => {
  const { edit, create } = useNavigation();
  const { mutate: deleteReason } = useDelete();

  const { tableProps, tableQueryResult } = useTable<CancellationReason>({
    resource: 'admin/cancellation-reasons',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'sortOrder', order: 'asc' }],
    },
  });

  const handleToggle = async (id: number, currentValue: boolean) => {
    try {
      await apiClient.put(`/admin/cancellation-reasons/${id}`, { isActive: !currentValue });
      message.success(currentValue ? 'Sabab yashirildi' : 'Sabab faollashtirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const moveUp = async (record: CancellationReason) => {
    const currentData = tableProps.dataSource as CancellationReason[] | undefined;
    if (!currentData) return;

    const currentIndex = currentData.findIndex(item => item.id === record.id);
    if (currentIndex <= 0) return;

    const prevItem = currentData[currentIndex - 1];

    try {
      await Promise.all([
        apiClient.put(`/admin/cancellation-reasons/${record.id}`, { sortOrder: prevItem.sortOrder }),
        apiClient.put(`/admin/cancellation-reasons/${prevItem.id}`, { sortOrder: record.sortOrder }),
      ]);
      message.success('Tartib o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const moveDown = async (record: CancellationReason) => {
    const currentData = tableProps.dataSource as CancellationReason[] | undefined;
    if (!currentData) return;

    const currentIndex = currentData.findIndex(item => item.id === record.id);
    if (currentIndex >= currentData.length - 1) return;

    const nextItem = currentData[currentIndex + 1];

    try {
      await Promise.all([
        apiClient.put(`/admin/cancellation-reasons/${record.id}`, { sortOrder: nextItem.sortOrder }),
        apiClient.put(`/admin/cancellation-reasons/${nextItem.id}`, { sortOrder: record.sortOrder }),
      ]);
      message.success('Tartib o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    {
      title: 'Tartib',
      key: 'reorder',
      width: 100,
      render: (_: unknown, record: CancellationReason) => {
        const currentData = tableProps.dataSource as CancellationReason[] | undefined;
        const currentIndex = currentData?.findIndex(item => item.id === record.id) ?? -1;
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === (currentData?.length ?? 0) - 1;

        return (
          <Space size="small">
            <Tooltip title="Yuqoriga ko'tarish">
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={isFirst}
                onClick={() => moveUp(record)}
              />
            </Tooltip>
            <Tooltip title="Pastga tushirish">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={isLast}
                onClick={() => moveDown(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Turi',
      dataIndex: 'actorType',
      key: 'actorType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'CLIENT' ? 'blue' : 'green'}>
          {type === 'CLIENT' ? 'Mijoz' : 'Haydovchi'}
        </Tag>
      ),
    },
    {
      title: 'Sabab (UZ)',
      dataIndex: 'reasonUz',
      key: 'reasonUz',
      ellipsis: true,
    },
    {
      title: 'Sabab (RU)',
      dataIndex: 'reasonRu',
      key: 'reasonRu',
      ellipsis: true,
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (active: boolean, record: CancellationReason) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id, active)}
          checkedChildren="Faol"
          unCheckedChildren="Yashirin"
          size="small"
        />
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: CancellationReason) => (
        <Space>
          <Tooltip title="Tahrirlash">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => edit('admin/cancellation-reasons', record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            onConfirm={() =>
              deleteReason({
                resource: 'admin/cancellation-reasons',
                id: record.id,
              })
            }
          >
            <Tooltip title="O'chirish">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <List
      title="Bekor qilish sabablari"
      headerButtons={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => create('admin/cancellation-reasons')}
        >
          Yangi sabab
        </Button>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
